import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box, Button, CircularProgress, Grid, InputAdornment, List, ListItemButton, ListItemText, MenuItem, Paper, Skeleton,
  Stack, TextField, ToggleButton, ToggleButtonGroup, Typography, alpha,
} from '@mui/material';
import { Search, CheckRounded, EventBusyOutlined } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import {
  timetableService, AvailabilityKind, AvailabilityRules, AvailabilityRow, LessonsOverview,
} from '../../../services/timetableService';
import EmptyState from '../../common/EmptyState';
import { ToneChip } from '../../common/StatusChip';
import { ROOM_TYPE_LABELS, fixLink, periodsOn, sectionLabel, weeklyPeriods } from '../timetableUtils';
import AvailabilityGrid, { Brush } from './AvailabilityGrid';

interface AvailabilityTabProps {
  sessionId: string;
  overview: LessonsOverview;
  canWrite: boolean;
  onChanged: () => void;
}

interface Entity { id: string; name: string; detail?: string; load?: number }

const NONE: AvailabilityRules = { unavailable: [], preferNot: [], maxPerDay: null };
const KIND_LABELS: Record<AvailabilityKind, string> = { teacher: 'Teachers', section: 'Classes', room: 'Rooms' };
const BRUSHES: { value: Brush; label: string; tone: 'error' | 'warning' | 'success' }[] = [
  { value: 'unavailable', label: 'Unavailable', tone: 'error' },
  { value: 'preferNot', label: 'Prefer not', tone: 'warning' },
  { value: 'available', label: 'Available', tone: 'success' },
];
const SAVE_DELAY = 600;

const hasRules = (r?: AvailabilityRules) => !!r && (r.unavailable.length > 0 || r.preferNot.length > 0 || !!r.maxPerDay);

/** When teachers, classes and special rooms can be timetabled. */
const AvailabilityTab: React.FC<AvailabilityTabProps> = ({ sessionId, overview, canWrite, onChanged }) => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [kind, setKind] = useState<AvailabilityKind>('teacher');
  const [selected, setSelected] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [brush, setBrush] = useState<Brush>('unavailable');
  const [rules, setRules] = useState<Map<string, AvailabilityRules>>(new Map());
  const [loading, setLoading] = useState(true);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const pending = useRef<{ kind: AvailabilityKind; id: string; rules: AvailabilityRules } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let live = true;
    timetableService.getAvailability(sessionId)
      .then((rows: AvailabilityRow[]) => live && setRules(new Map(rows.map((r) => [`${r.kind}:${r.refId}`, { unavailable: r.unavailable, preferNot: r.preferNot, maxPerDay: r.maxPerDay ?? null }]))))
      .catch((err) => live && enqueueSnackbar(err?.message || 'Could not load availability', { variant: 'error' }))
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [sessionId, enqueueSnackbar]);

  const entities = useMemo<Record<AvailabilityKind, Entity[]>>(() => {
    const teachers = new Map<string, Entity>();
    const sections = new Map<string, Entity & { order: number }>();
    for (const l of overview.lessons) {
      if (l.teacherId) {
        const t = teachers.get(l.teacherId) ?? { id: l.teacherId, name: l.teacherName ?? '', load: 0 };
        t.load! += l.count;
        teachers.set(l.teacherId, t);
      }
      const id = l.sectionInstanceId ?? l.classId;
      const s = sections.get(id) ?? { id, name: sectionLabel(l), load: 0, order: l.classOrder };
      s.load! += l.count;
      sections.set(id, s);
    }
    return {
      teacher: Array.from(teachers.values()).sort((a, b) => a.name.localeCompare(b.name)),
      section: Array.from(sections.values()).sort((a, b) => a.order - b.order || a.name.localeCompare(b.name)),
      room: overview.rooms.filter((r) => r.type !== 'classroom').map((r) => ({ id: r._id, name: r.name, detail: ROOM_TYPE_LABELS[r.type] ?? r.type })),
    };
  }, [overview.lessons, overview.rooms]);

  const flush = useCallback(async () => {
    if (timer.current) { clearTimeout(timer.current); timer.current = null; }
    const p = pending.current;
    if (!p) return;
    pending.current = null;
    setSaveState('saving');
    try {
      await timetableService.saveAvailability(sessionId, p.kind, p.id, p.rules);
      setSaveState('saved');
      onChanged();
    } catch (err: any) {
      setSaveState('idle');
      enqueueSnackbar(err?.message || 'Could not save availability', { variant: 'error' });
    }
  }, [sessionId, onChanged, enqueueSnackbar]);

  // Save what's pending before leaving the tab.
  useEffect(() => () => { flush(); }, [flush]);

  const list = entities[kind];
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? list.filter((e) => e.name.toLowerCase().includes(q)) : list;
  }, [list, query]);

  // Keep a selection that exists in the current list.
  useEffect(() => {
    if (!list.some((e) => e.id === selected)) setSelected(list[0]?.id ?? null);
  }, [list, selected]);

  const select = (id: string) => { flush(); setSelected(id); setSaveState('idle'); };
  const switchKind = (k: AvailabilityKind) => { flush(); setKind(k); setQuery(''); setSaveState('idle'); };

  const entity = list.find((e) => e.id === selected) ?? null;
  const ruleKey = entity ? `${kind}:${entity.id}` : '';
  const current = rules.get(ruleKey) ?? NONE;

  const update = useCallback((next: Partial<AvailabilityRules>) => {
    if (!entity) return;
    const merged = { ...(rules.get(`${kind}:${entity.id}`) ?? NONE), ...next };
    setRules((m) => new Map(m).set(`${kind}:${entity.id}`, merged));
    pending.current = { kind, id: entity.id, rules: merged };
    setSaveState('idle');
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(flush, SAVE_DELAY);
  }, [entity, kind, rules, flush]);

  const config = overview.config;
  if (!config) {
    return (
      <EmptyState
        icon={<EventBusyOutlined />}
        title="Set up the school day first"
        description="Availability is painted on the school day's periods."
        action={{ label: 'Set up the school day', onClick: () => navigate(fixLink('school_day', sessionId).to) }}
      />
    );
  }

  const blocked = current.unavailable.filter((s) => config.days.includes(s.day) && s.period <= periodsOn(config, s.day)).length;
  const free = weeklyPeriods(config) - blocked;
  const overloaded = entity?.load !== undefined && entity.load > free;

  return (
    <Grid container spacing={2.5}>
      <Grid size={{ xs: 12, md: 4, lg: 3 }}>
        <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden', position: { md: 'sticky' }, top: 88 }}>
          <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <ToggleButtonGroup
              exclusive fullWidth size="small" value={kind}
              onChange={(_, v: AvailabilityKind | null) => v && switchKind(v)}
              sx={{ mb: 1.25, '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600 } }}
            >
              {(Object.keys(KIND_LABELS) as AvailabilityKind[]).map((k) => <ToggleButton key={k} value={k}>{KIND_LABELS[k]}</ToggleButton>)}
            </ToggleButtonGroup>
            <TextField
              size="small" fullWidth placeholder={`Search ${KIND_LABELS[kind].toLowerCase()}`} value={query}
              onChange={(e) => setQuery(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" sx={{ color: 'text.secondary' }} /></InputAdornment> }}
            />
          </Box>
          <List dense disablePadding sx={{ maxHeight: { md: 'calc(100vh - 300px)' }, overflowY: 'auto', py: 0.5 }}>
            {loading
              ? [0, 1, 2, 3, 4].map((i) => <Skeleton key={i} variant="rounded" height={36} sx={{ mx: 1.5, my: 0.75 }} />)
              : filtered.map((e) => (
                <ListItemButton key={e.id} selected={e.id === selected} onClick={() => select(e.id)} sx={{ mx: 0.75, borderRadius: '8px' }}>
                  <ListItemText
                    primary={e.name}
                    secondary={e.detail ?? (e.load !== undefined ? `${e.load} periods a week` : undefined)}
                    primaryTypographyProps={{ variant: 'body2', fontWeight: e.id === selected ? 700 : 500, noWrap: true }}
                    secondaryTypographyProps={{ variant: 'caption' }}
                  />
                  {hasRules(rules.get(`${kind}:${e.id}`)) && (
                    <Box sx={(theme) => ({ width: 8, height: 8, borderRadius: '50%', bgcolor: theme.palette.warning.main, flexShrink: 0, ml: 1 })} aria-label="Has availability rules" />
                  )}
                </ListItemButton>
              ))}
            {!loading && filtered.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                {kind === 'room' ? 'No special rooms. Add labs and other rooms in Rooms.' : 'Nothing matches.'}
              </Typography>
            )}
          </List>
        </Paper>
      </Grid>

      <Grid size={{ xs: 12, md: 8, lg: 9 }}>
        {!entity ? (
          <EmptyState icon={<EventBusyOutlined />} title="Pick someone on the left" description="Paint the periods they can't be timetabled, or would rather not be." />
        ) : (
          <Paper variant="outlined" sx={{ borderRadius: 2.5, p: 2.5 }}>
            <Stack direction="row" alignItems="flex-start" spacing={2} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Typography variant="h6" fontWeight={700}>{entity.name}</Typography>
                {entity.load !== undefined ? (
                  <Typography variant="body2" color={overloaded ? 'error.main' : 'text.secondary'}>
                    {kind === 'teacher' ? 'Teaches' : 'Has'} {entity.load} periods a week, free for {free}{overloaded ? '. Not enough time.' : ''}
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">Free for {free} of {weeklyPeriods(config)} periods a week</Typography>
                )}
              </Box>
              <Box sx={{ minHeight: 24, display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
                {saveState === 'saving' && <><CircularProgress size={12} thickness={5} /><Typography variant="caption">Saving</Typography></>}
                {saveState === 'saved' && <><CheckRounded sx={{ fontSize: 16 }} color="success" /><Typography variant="caption">Saved</Typography></>}
              </Box>
            </Stack>

            {canWrite && (
              <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                <ToggleButtonGroup
                  exclusive size="small" value={brush} onChange={(_, v: Brush | null) => v && setBrush(v)}
                  sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 600, gap: 0.75, px: 1.5 } }}
                >
                  {BRUSHES.map((b) => (
                    <ToggleButton key={b.value} value={b.value}>
                      <Box sx={(theme) => ({ width: 12, height: 12, borderRadius: '4px', bgcolor: alpha(theme.palette[b.tone].main, b.value === 'available' ? 0.15 : 0.45), border: `1px solid ${alpha(theme.palette[b.tone].main, 0.6)}` })} />
                      {b.label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                {kind !== 'room' && (
                  <TextField
                    select size="small" label="Most periods a day" value={current.maxPerDay ?? ''} sx={{ minWidth: 170 }}
                    onChange={(e) => update({ maxPerDay: e.target.value === '' ? null : Number(e.target.value) })}
                    SelectProps={{ displayEmpty: true, renderValue: (v) => (v === '' ? 'No limit' : String(v)) }}
                    InputLabelProps={{ shrink: true }}
                  >
                    <MenuItem value="">No limit</MenuItem>
                    {Array.from({ length: Math.max(...config.days.map((d) => periodsOn(config, d))) }, (_, i) => i + 1).map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                  </TextField>
                )}
                {hasRules(current) && (
                  <Button size="small" color="inherit" sx={{ textTransform: 'none', color: 'text.secondary', ml: 'auto' }} onClick={() => update(NONE)}>
                    Clear all
                  </Button>
                )}
              </Stack>
            )}

            <AvailabilityGrid
              grid={config}
              unavailable={current.unavailable}
              preferNot={current.preferNot}
              brush={brush}
              readOnly={!canWrite}
              onChange={update}
            />

            <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                Click or drag across periods to paint. Click a day or a period number to paint the whole row or column.
              </Typography>
              {current.unavailable.length > 0 && <ToneChip label={`${current.unavailable.length} unavailable`} tone="error" />}
              {current.preferNot.length > 0 && <ToneChip label={`${current.preferNot.length} prefer not`} tone="warning" />}
            </Stack>
          </Paper>
        )}
      </Grid>
    </Grid>
  );
};

export default AvailabilityTab;
